import { Link } from "gatsby"
import React, { useEffect, useState, useCallback } from "react"
import { graphql, useStaticQuery } from "gatsby"
import Button from "./button"
import NavFeatureLink from "./new/navFeatureLink/navFeatureLink"
import NavPartnerLink from "./new/navPartnerLink/navPartnerLink"
import logo from "../assets/icons/logo.svg"

// Constants for dropdown data
const USE_CASES = [
	{
		to: "/launch-your-new-marketplace-with-solid-foundations",
		text: "Launch a New Marketplace",
	},
	{
		to: "/expand-product-catalog-with-zero-inventory-risk",
		text: "Expand Product Catalog",
	},
	{
		to: "/scale-your-marketplace-efficiently-to-future-proof-your-business",
		text: "Scale Marketplace Operations",
	},
]

const FEATURE_CATEGORIES = [
	{
		to: "/catalog-management",
		title: "Catalog Management",
		description:
			"Upload and sync the inventory to your store automatically",
	},
	{
		to: "/order-management",
		title: "Order Management",
		description:
			"Automate order routing to focus on growing your marketplace",
	},
	{
		to: "/shipping-workflows",
		title: "Shipping Workflows",
		description:
			"Optimize your shipping strategy with pre-built integrations",
	},
	{
		to: "/payments-automation",
		title: "Payments Automation",
		description:
			"Manage all of your pricing, commissions and payment needs",
	},
	{
		to: "/returns-management",
		title: "Returns Management",
		description: "Ensure customer loyalty with an efficient returns system",
	},
	{
		to: "/vendor-onboarding",
		title: "Vendor Onboarding",
		description:
			"Add new vendors to your store seamlessly to increase offer",
	},
	{
		to: "/api-capabilities",
		title: "API Capabilities",
		description:
			"Customize your marketplace according to your business needs",
	},
	{
		to: "/other-features",
		title: "Other Features",
		description:
			"Leverage a wide range of features to grow your marketplace",
	},
]

const PARTNER_CATEGORIES = [
	{
		to: "/partners/store#filter",
		title: "Store",
		description: "Connect Onport to a supported Storefront",
		icons: ["elastic-path", "shopify", "bigcommerce"],
	},
	{
		to: "/partners/dropship#filter",
		title: "Dropship",
		description: "Integrations for your dropshipping operations",
		icons: ["shopify", "google-drive", "bigcommerce"],
	},
	{
		to: "/partners/shipping#filter",
		title: "Shipping",
		description: "Shipping integrations for your marketplace",
		icons: ["shippo", "shippypro", "usps"],
	},
	{
		to: "/partners/payments#filter",
		title: "Payments",
		description: "Streamline your payment processes with ease",
		icons: ["paypal", "stripe", "transferwise"],
	},
	{
		to: "/partners/accounting#filter",
		title: "Accounting",
		description: "Easily keep on top of your financial obligations",
		icons: ["quickbooks", "xero", "taxjar"],
	},
	{
		to: "/partners/automation#filter",
		title: "Automation",
		description: "Keep your marketplace running smoothly",
		icons: ["intercom", "slack", "mailgun"],
	},
	{
		to: "/partners/ecosystem#filter",
		title: "Ecosystem",
		description: "Ecosystem partners for your marketplace",
		icons: ["carriboo", "shopbox-ai", "patchworks"],
	},
	{
		to: "/partners#filter",
		title: "All Partners",
		description: "Explore all our partners",
		icons: [],
	},
]

const Navbar = ({ style = {} }) => {
	const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
	const [hasScrolled, setHasScrolled] = useState(false)
	const currentUrl = typeof window !== "undefined" ? window.location.href : ""

	const data = useStaticQuery(graphql`
		query {
			allContentfulFeature {
				edges {
					node {
						title
						slug
						icon
						featured
						summary {
							summary
						}
					}
				}
			}
		}
	`)

	const handleScroll = useCallback(() => {
		setHasScrolled(window.scrollY > 50)
	}, [])

	useEffect(() => {
		if (typeof window !== "undefined") {
			window.addEventListener("scroll", handleScroll)
			return () => window.removeEventListener("scroll", handleScroll)
		}
	}, [handleScroll])

	const toggleMobileNav = useCallback(() => {
		setIsMobileNavOpen(prev => !prev)
		document.body.style.overflow = isMobileNavOpen ? "" : "hidden"
	}, [isMobileNavOpen])

	const renderDropdownLinks = links =>
		links.map(({ to, text }) => (
			<Link key={to} to={to} className="link__dropdown ml-4">
				<span>{text}</span>
			</Link>
		))


	return (
		<div
			style={{
				position: style.position || "sticky",
				top: "38px",
				zIndex: "10000000",
			}}
		>
			{/* Desktop Navigation */}
			<nav
				className={`nav ${style.classes} ${
					hasScrolled ? "bg-dark-blue" : ""
				}`}
			>
				<div className="container-fluid nav-container">
					<div className="nav__left">
						<Link to="/" className="title">
							<img
								src={logo}
								className="logo mr-5"
								alt="Onport logo"
							/>
						</Link>

						<div className="nav-links">
							{/* Use Cases Dropdown */}
							<div className="dropdown nav-item d-flex align-items-center">
								<span
									className="link link__resources"
									data-toggle="dropdown"
								>
									Use cases
								</span>
								<div
									className="dropdown-menu fade-up"
									style={{ width: "228px" }}
								>
									{renderDropdownLinks(USE_CASES)}
								</div>
							</div>

							{/* Features Mega Menu */}
							<div className="dropdown nav-item d-flex align-items-center">
								<Link to="/features" className="link">
									<span
										className="link link__resources mx-0"
										data-toggle="dropdown"
									>
										Features
									</span>
								</Link>
								<div
									className="dropdown-menu fade-up"
									style={{
										background: "rgba(0,0,0,0)",
										height: "260px",
									}}
								>
									<div
										className="d-flex"
										style={{
											background: "white",
											height: "260px",
											width: "970px",
											borderRadius: "6px",
										}}
									>
										<div className="w-100 row py-2 pl-4">
											{FEATURE_CATEGORIES.map(
												NavFeatureLink
											)}
										</div>
									</div>
								</div>
							</div>

							{/* Other Main Links */}
							<Link
								to="/composable"
								className="link"
								activeClassName="link-active"
							>
								Composable
							</Link>
							<Link
								to="/enterprise"
								className="link"
								activeClassName="link-active"
							>
								Enterprise
							</Link>

							{/* Partners Mega Menu */}
							<div className="dropdown nav-item d-flex align-items-center">
								<Link to="/partners" className="link">
									<span
										className="link link__resources mx-0"
										data-toggle="dropdown"
									>
										Partners
									</span>
								</Link>
								<div
									className="dropdown-menu fade-up"
									style={{
										background: "rgba(0,0,0,0)",
										height: "320px",
									}}
								>
									<div
										className="d-flex"
										style={{
											background: "white",
											height: "320px",
											width: "800px",
											borderRadius: "6px",
										}}
									>
										<div className="w-100 row py-2 pl-4">
												{PARTNER_CATEGORIES.map(
													NavPartnerLink
												)}
										</div>
									</div>
								</div>
							</div>

							{/* Simple Links */}
							<Link
								to="/casestudies"
								className="link"
								activeClassName="link-active"
							>
								Case Studies
							</Link>
							<Link
								to="/blog"
								className="link"
								activeClassName="link-active"
							>
								Blog
							</Link>
							<Link
								to="/contacts/sellers"
								className="link"
								activeClassName="link-active"
							>
								Sellers
							</Link>
						</div>
					</div>

					<div className="nav__right">
						{currentUrl !== "http://onport.com/" && (
							<Link
								className="btnOn dark-bg-alt mr-3"
								to="/contacts/book-demo"
							>
								Book Demo
							</Link>
						)}
						<Button
							style={{
								shape: "text-light",
								text: "Sign in",
								href: "https://app.jetti.io/login",
							}}
						/>
					</div>
				</div>
			</nav>

			{/* Mobile Navigation */}
			<nav className={`nav-mobile position-absolute ${style.classes} ${
					hasScrolled ? "bg-dark-blue" : ""
				}`} >
				<div
					className="container-fluid nav-container"
					style={{ position: "relative" }}
				>
					<div>
						<Link
							to="/"
							className="title"
							onClick={() => setIsMobileNavOpen(false)}
						>
							<img
								src={logo}
								className="logo"
								alt="Onport logo"
							/>
						</Link>
					</div>

					{/* Fixed position toggle buttons */}
					<div
						style={{
							position: "fixed",
							top: "50px",
							right: "20px",
							zIndex: 1001,
							width: "40px",
							height: "40px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<button
							className="open-btn"
							onClick={toggleMobileNav}
							aria-label="Open menu"
							style={{
								display: isMobileNavOpen ? "none" : "block",
								fontSize: "2.5rem",
								background: "none",
								border: "none",
								color: "white",
								cursor: "pointer",
								transition: "color 0.3s ease",
							}}
						>
							≡
						</button>
						<button
							className="close-btn"
							onClick={toggleMobileNav}
							aria-label="Close menu"
							style={{
								display: isMobileNavOpen ? "block" : "none",
								fontSize: "2.5rem",
								background: "none",
								border: "none",
								color: "#333",
								cursor: "pointer",
								position: "absolute",
								right: "0",
							}}
						>
							×
						</button>
					</div>

					{/* Mobile menu content */}
					<div
						className={`nav-links-mobile ${
							isMobileNavOpen ? "open" : ""
						}`}
						style={{
							position: "fixed",
							top: "0",
							right: "0",
							width: "100%",
							height: "100vh",
							backgroundColor: "white",
							padding: "80px 30px",
							boxSizing: "border-box",
							transform: isMobileNavOpen
								? "translateX(0)"
								: "translateX(100%)",
							transition: "transform 0.3s ease-out",
							zIndex: 1000,
							overflowY: "auto",
						}}
					>
						<div
							className="nav-links-mobile__links"
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: "12px",
							}}
						>
							{/* All root links from desktop */}
							<Link
								to="/"
								className="link"
								activeClassName="link-active"
								onClick={toggleMobileNav}
								style={{
									fontSize: "1.2rem",
									fontWeight: "600",
									color: "#333",
									textDecoration: "none",
								}}
							>
								Home
							</Link>

							<div className="mobile-dropdown text-center">
								<span
									className="link"
									style={{
										fontSize: "1.2rem",
										fontWeight: "600",
										color: "#333",
										cursor: "pointer",
									}}
								>
									Use Cases
								</span>
								<div
									style={{
										marginLeft: "15px",
										marginTop: "10px",
									}}
								>
									{USE_CASES.map(({ to, text }) => (
										<Link
											key={to}
											to={to}
											className="link"
											onClick={toggleMobileNav}
											style={{
												display: "block",
												padding: "8px 0",
												fontSize: "1rem",
												color: "#666",
											}}
										>
											{text}
										</Link>
									))}
								</div>
							</div>

							<Link
								to="/features"
								className="link"
								activeClassName="link-active"
								onClick={toggleMobileNav}
								style={{
									fontSize: "1.2rem",
									fontWeight: "600",
									color: "#333",
								}}
							>
								Features
							</Link>

							<Link
								to="/composable"
								className="link"
								activeClassName="link-active"
								onClick={toggleMobileNav}
								style={{
									fontSize: "1.2rem",
									fontWeight: "600",
									color: "#333",
								}}
							>
								Composable
							</Link>

							<Link
								to="/enterprise"
								className="link"
								activeClassName="link-active"
								onClick={toggleMobileNav}
								style={{
									fontSize: "1.2rem",
									fontWeight: "600",
									color: "#333",
								}}
							>
								Enterprise
							</Link>

							<Link
								to="/partners"
								className="link"
								activeClassName="link-active"
								onClick={toggleMobileNav}
								style={{
									fontSize: "1.2rem",
									fontWeight: "600",
									color: "#333",
								}}
							>
								Partners
							</Link>

							<Link
								to="/casestudies"
								className="link"
								activeClassName="link-active"
								onClick={toggleMobileNav}
								style={{
									fontSize: "1.2rem",
									fontWeight: "600",
									color: "#333",
								}}
							>
								Case Studies
							</Link>

							<Link
								to="/blog"
								className="link"
								activeClassName="link-active"
								onClick={toggleMobileNav}
								style={{
									fontSize: "1.2rem",
									fontWeight: "600",
									color: "#333",
								}}
							>
								Blog
							</Link>

							<Link
								to="/contacts/sellers"
								className="link"
								activeClassName="link-active"
								onClick={toggleMobileNav}
								style={{
									fontSize: "1.2rem",
									fontWeight: "600",
									color: "#333",
								}}
							>
								Sellers
							</Link>
							<div
								style={{
									marginTop: "30px",
									display: "flex",
									gap: "15px",
									alignItems: "center",
								}}
							>
								<Link
									to="/contacts/book-demo"
									className="btnOn dark-bg-alt"
									onClick={toggleMobileNav}
									style={{
										padding: "12px 20px",
										borderRadius: "4px",
										background: "#0066ff",
										color: "white",
										textDecoration: "none",
										fontSize: "1rem",
									}}
								>
									Book Demo
								</Link>
								<Button
									style={{
										shape: "text-dark",
										text: "Sign in",
										href: "https://app.jetti.io/login",
									}}
									onClick={toggleMobileNav}
								/>
							</div>
						</div>
					</div>
				</div>
			</nav>
		</div>
	)
}

export default Navbar
